import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { ReportListItem } from '@treasury/domain/reports/report-list-item.entity';
import { ReportingService } from '@treasury/domain/reports/reports.service';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import {
    circleXmarkIcon,
    documentArrowRightIcon,
    ellipsisIcon,
    emptyStarIcon,
    filePlusIcon,
    filledStarIcon,
} from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-context-menu';
import '@treasury/presentation/components/tm-icon';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-tabs';
import '@vaadin/grid';
import { columnBodyRenderer, GridColumnBodyLitRenderer } from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-column.js';
import { css, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../../reports/partials/report-save-custom';

export enum ReportAction {
    RUN_REPORT = 'Run Report',
    EDIT_REPORT = 'Edit Report',
    CREATE_CUSTOM_REPORT = 'Create Custom Report',
    MAKE_FAVORITE = 'Make a Favorite',
    UNFAVORITE = 'Unfavorite',
    DELETE_REPORT = 'Delete Report',
}

export const tagName = 'reports-widget';
@customElement(tagName)
export class ReportsWidget extends TmBaseComponent {
    @property({ type: Boolean, reflect: true })
    public rounded = true;

    @InjectProperty()
    declare private reportingService: ReportingService;

    @InjectProperty()
    declare private navigationService: NavigationService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @state()
    private reports: ReportListItem[] = [];

    @state()
    private visibleReports: ReportListItem[] = [];

    @state()
    private tabs: { label: string; id: string }[] = [
        { label: 'All', id: 'all' },
        { label: 'Custom', id: 'custom' },
        { label: 'Favorite', id: 'favorite' },
    ];

    @state()
    private activeTab = 'all';

    @state()
    private loading = false;

    @state()
    private showSaveReportDialog = false;

    @state()
    private selectedReport?: ReportListItem;

    protected async firstUpdated() {
        await this.getReports();
    }

    protected render() {
        return html`<tm-card class="h-[355px]" .rounded=${this.rounded}
                >${this.renderHeader()}${this.renderLoader()}${this.renderReportsTable()}</tm-card
            ><report-save-custom
                ?open=${this.showSaveReportDialog}
                .report=${this.selectedReport}
                @close=${() => {
                    this.showSaveReportDialog = false;
                }}
                @saved-new-report=${() => {
                    this.getReports();
                    this.showSaveReportDialog = false;
                }}
            ></report-save-custom>`;
    }

    protected async getReports() {
        this.loading = true;
        this.reports = await this.reportingService.getReports();
        this.visibleReports = this.reports;
        this.loading = false;
    }

    private filterReports() {
        if (this.activeTab === 'all') {
            this.visibleReports = this.reports;
        } else if (this.activeTab === 'custom') {
            this.visibleReports = this.reports.filter(report => !report.isStandardReport);
        } else {
            this.visibleReports = this.reports.filter(report => report.isFavorite);
        }
    }

    private async deleteReport(report: ReportListItem) {
        try {
            await this.reportingService.deleteReport(report);
            this.reports = this.reports.filter(r => r.id !== report.id);
            this.filterReports();
            this.notificationService.renderSuccess('Report deleted successfully');
        } catch {
            this.notificationService.renderError('Failed to delete report');
        }
    }

    private async updateFavoriteStatus(report: ReportListItem) {
        try {
            this.loading = true;
            report.isFavorite = !report.isFavorite;
            await this.reportingService.updateFavoriteReportStatus(report, report.isFavorite);
            this.notificationService.renderSuccess('Favorite status updated successfully');
            this.filterReports();
        } catch {
            report.isFavorite = !report.isFavorite;
            this.notificationService.renderError('Failed to update favorite status');
        } finally {
            this.loading = false;
        }
    }

    private renderLoader() {
        if (!this.loading) {
            return nothing;
        }
        return html`<div class="flex items-center justify-center h-[285px]">
            <tm-loader card></tm-loader>
        </div>`;
    }

    private createActionItem(iconName: TemplateResult, text: string) {
        const item = document.createElement('vaadin-context-menu-item');
        const wrapper = document.createElement('div');
        const icon = document.createElement('tm-icon');

        icon.style.marginRight = '5px';
        icon.icon = iconName;
        wrapper.classList.add('flex', 'items-center');
        wrapper.appendChild(icon);
        item.appendChild(wrapper);
        if (text) {
            wrapper.appendChild(document.createTextNode(text));
        }
        return item;
    }

    private renderHeader() {
        return html`<div class="flex justify-between items-center">
            <span class="font-medium text-header">Reporting</span
            ><tm-tabs
                .tabs=${this.tabs}
                .activeTab=${this.activeTab}
                @switchTab=${(e: CustomEvent) => {
                    this.activeTab = e.detail.activeTab;
                    this.filterReports();
                }}
            ></tm-tabs>
        </div>`;
    }

    private renderReportColumn(report: ReportListItem) {
        const reportActions = [
            {
                component: this.createActionItem(documentArrowRightIcon, ReportAction.RUN_REPORT),
            },
            {
                component: report.isStandardReport
                    ? this.createActionItem(filePlusIcon, ReportAction.CREATE_CUSTOM_REPORT)
                    : this.createActionItem(filePlusIcon, ReportAction.EDIT_REPORT),
            },
            {
                component: report.isFavorite
                    ? this.createActionItem(emptyStarIcon, ReportAction.UNFAVORITE)
                    : this.createActionItem(filledStarIcon, ReportAction.MAKE_FAVORITE),
            },
        ];
        if (!report.isStandardReport) {
            reportActions.push({
                component: this.createActionItem(circleXmarkIcon, ReportAction.DELETE_REPORT),
            });
        }
        return html`<div class="flex justify-between items-center border-l-4 border-[--brand-100]">
            <div
                class="flex flex-col ml-2"
                @click=${() => {
                    this.navigationService.navigate(`/reports/${report.reportType}/${report.id}`, {
                        action: ReportAction.RUN_REPORT,
                    });
                    this.dispatchEvent(new CustomEvent('report-action-taken'));
                }}
            >
                <span class="text-sm">${report.name}</span>
                <span class="text-xs text-secondary">${report.description}</span>
            </div>
            <div class="pr-4 flex items-end justify-center">
                <tm-button
                    importance="tertiary"
                    class="h-[45px] mr-2"
                    @click=${() => {
                        this.updateFavoriteStatus(report);
                    }}
                    ><tm-icon .icon=${report.isFavorite ? filledStarIcon : emptyStarIcon}></tm-icon
                ></tm-button>
                <tm-context-menu
                    .openOn=${'click'}
                    .items=${reportActions}
                    @item-selected=${(e: CustomEvent) => {
                        this.selectedReport = report;
                        const action = e.detail.value.component.textContent;
                        if (action === ReportAction.DELETE_REPORT) {
                            this.deleteReport(report);
                        }
                        if (
                            action === ReportAction.MAKE_FAVORITE ||
                            action === ReportAction.UNFAVORITE
                        ) {
                            this.updateFavoriteStatus(report);
                        }
                        if (action === ReportAction.RUN_REPORT) {
                            this.navigationService.navigate(
                                `/reports/${report.reportType}/${report.id}`,
                                { action: action }
                            );
                        }
                        if (
                            action === ReportAction.EDIT_REPORT ||
                            action === ReportAction.CREATE_CUSTOM_REPORT
                        ) {
                            this.showSaveReportDialog = true;
                        }
                        this.dispatchEvent(
                            new CustomEvent('report-action-taken', {
                                detail: { report: report, action },
                            })
                        );
                    }}
                >
                    <tm-button importance="tertiary"
                        ><tm-icon .icon=${ellipsisIcon}></tm-icon
                    ></tm-button>
                </tm-context-menu>
            </div>
        </div>`;
    }

    private renderReportsTable() {
        if (!this.visibleReports.length && !this.loading) {
            return html`<div class="text-center w-full py-5 h-[285px]">
                No reports are currently available.
            </div>`;
        }
        if (this.loading) {
            return nothing;
        }

        return html`<vaadin-grid class="-mx-4" .items=${this.visibleReports}>
            <vaadin-grid-column
                ${columnBodyRenderer(
                    this.renderReportColumn as GridColumnBodyLitRenderer<ReportListItem>
                )}
            ></vaadin-grid-column>
        </vaadin-grid>`;
    }

    public static get styles() {
        return css`
            :host {
                display: block;
                --vaadin-grid-cell-padding: 0.25rem 0;
            }
            vaadin-grid {
                height: 285px;
            }

            vaadin-grid-cell-content {
                padding: 0;
            }
        `;
    }
}
